import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a number as Italian currency (EUR) with proper separators
 * Uses punto (.) as thousands separator and virgola (,) as decimal separator
 * Example: 3604.27 -> "3.604,27 €"
 */
export function formatPriceIT(
  price: number | string | null | undefined,
  options?: {
    decimals?: number;
    showCurrency?: boolean;
    fallback?: string;
  }
): string {
  const { decimals = 2, showCurrency = true, fallback = "Su richiesta" } = options || {};
  
  if (price === null || price === undefined || price === "") return fallback;
  
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(numPrice)) return fallback;
  
  const formatted = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  }).format(numPrice);
  
  return showCurrency ? `${formatted} €` : formatted;
}

/**
 * Formats a number with Italian locale separators (no currency symbol)
 * Example: 3604.27 -> "3.604,27"
 */
export function formatNumberIT(
  value: number | string | null | undefined,
  decimals: number = 2
): string {
  if (value === null || value === undefined || value === "") return "N/D";
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return "N/D";
  
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  }).format(numValue);
}

/**
 * Adds cache-busting parameter to image URLs for CMS content.
 * Uses the updatedAt timestamp if provided, otherwise uses a version parameter.
 * This ensures browsers fetch fresh images after CMS updates.
 * 
 * @param url - The image URL
 * @param updatedAt - Optional timestamp (Date, string, or number) for cache-busting
 * @returns URL with cache-busting parameter
 */
export function cacheBustImageUrl(
  url: string | null | undefined,
  updatedAt?: Date | string | number | null
): string {
  if (!url) return '';
  
  // Don't add cache-busting to data URLs or already busted URLs
  if (url.startsWith('data:') || url.includes('_cb=')) {
    return url;
  }
  
  let version: string;
  
  if (updatedAt) {
    // Use the provided timestamp
    if (updatedAt instanceof Date) {
      version = updatedAt.getTime().toString();
    } else if (typeof updatedAt === 'string') {
      version = new Date(updatedAt).getTime().toString();
    } else {
      version = updatedAt.toString();
    }
  } else {
    // Use a 5-minute window version (changes every 5 minutes)
    // This matches our cache TTL and ensures fresh content while allowing some caching
    version = Math.floor(Date.now() / (5 * 60 * 1000)).toString();
  }
  
  // Add cache-busting parameter
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_cb=${version}`;
}
